import { defineStore } from 'pinia'
import th from '@tillhub/javascript-sdk'
import safeGet from 'just-safe-get'
import pRetry from 'p-retry'
import auth, {
  getUserId,
  getClientAccountId,
  getIsFirstLoginDone
} from '@shared/plugins/auth'
import { getBaseUrl } from '@shared/utils/baseUrlProvider.js'
import { license } from '@shared/constants'
import { isUnifiedCommerce, isKeycloackEnabled } from '@shared/constants/index'
import { useAppConfigStore } from '@shared/store/app-config'
import { useMessagesStore } from '@shared/store/messages'
import { usePosthog, FeatureFlags } from '@shared/plugins/posthog'
import jwt_decode from 'jwt-decode'
import { useLocationStore } from '@shared/store/location'

const LocalStorageKeys = {
  SessionUser: 'session_user',
  SupportUserId: 'support_user_id'
}

const LEGACY_VUEX_MAIN_KEY = 'main'

/** Prefer `session_user`; if missing, one-time migrate from legacy Vuex `main` → `Auth.username`. */
function readSessionUserFromStorage() {
  try {
    const ls = globalThis?.localStorage
    if (!ls) return null

    const current = ls.getItem(LocalStorageKeys.SessionUser)
    if (current) return current

    const mainRaw = ls.getItem(LEGACY_VUEX_MAIN_KEY)
    if (!mainRaw) return null

    const legacyUsername = JSON.parse(mainRaw)?.Auth?.username
    if (!legacyUsername) return null

    const value = String(legacyUsername)
    ls.setItem(LocalStorageKeys.SessionUser, value)
    return value
  } catch {
    return null
  }
}

/** Singleflight: concurrent `waitForMeData` share one in-flight `getMeData`. */
let waitForMeDataInflight = null

/** Clears singleflight state (e.g. between Vitest cases). Not used in production. */
export function resetWaitForMeDataInflight() {
  waitForMeDataInflight = null
}

let firstLoginAcknowledged = false

export function resetFirstLoginAcknowledged() {
  firstLoginAcknowledged = false
}

function getSwitchedClientAccount() {
  const active = globalThis?.localStorage?.getItem('clientAccountId') || null
  const own = getClientAccountId()
  return active && active !== own ? active : null
}

const initialState = () => ({
  // the tillhubClientAccount
  authenticated: false,
  me: null,
  user: null,
  token: globalThis.localStorage?.getItem('token') || null,
  imageUrl: null,
  email: null,
  // TODO: the username for sub users is not available from the token or from the me call yet. We store it in the local storage for now.
  username: readSessionUserFromStorage(),
  accountName: null,
  orgName: null,
  expiresAt: null,
  isSupport: null,
  features: {},
  role: null,
  staff: null,
  scopes: [],
  // the value from sub_user
  thUserId: null,
  // only these locations are allowed. If null none have been specified and we assume non-shall be restricted
  locations: null,
  whitelabel: null,
  isThInitialized: false,
  // Singleflight: concurrent `waitForMeData` share one in-flight `getMeData
  waitForMeDataInflight: null
})

// Reads the credentials Endor puts on the URL when it links a support agent
// straight to a merchant.
function readSupportUserParams() {
  const url = new URL(globalThis.location.href)
  // Extract client_account and support_token parameters from the URL
  const params = new URLSearchParams(url.search)
  const clientAccountParam = params.get('client_account')
  const supportTokenParam = params.get('support_token')
  const isUnzerId =
    clientAccountParam && clientAccountParam.substring(0, 2) === 'UZ'

  // supportTokenParam comes from Endor and we need isUnzerId for Insights
  const isSupportEntry =
    isUnifiedCommerce() &&
    clientAccountParam &&
    (supportTokenParam || isUnzerId)

  return {
    clientAccountParam,
    isSupportEntry: Boolean(isSupportEntry)
  }
}

// An agent who still has to log in comes back on the origin root, without the
// query string Endor sent them in with. Keep the account somewhere that
// survives that redirect, while it is still on the address.
export function captureSupportUserCredentials() {
  const { clientAccountParam, isSupportEntry } = readSupportUserParams()

  if (!isSupportEntry) {
    return
  }

  globalThis?.localStorage?.setItem(
    'support_client_account',
    clientAccountParam
  )
}

// Used in case of support users login. Extracts credentials from the URL coming from Endor.
async function getSupportUserCredentials(store) {
  const { clientAccountParam, isSupportEntry } = readSupportUserParams()

  const cachedSupportAccount =
    globalThis?.localStorage?.getItem('support_client_account') || null

  const isSupportUserUFC = cachedSupportAccount || isSupportEntry

  if (isSupportUserUFC) {
    // In Unified Commerce, a fresh client_account in the URL means the support
    // agent is intentionally opening a specific merchant, so it must win over any
    // account cached from a previous session (e.g. a tab closed without logging
    // out). Falling back to the cached value keeps the active merchant stable
    // during normal navigation once the URL params have been stripped after
    // login. Outside UFC we preserve the legacy behavior of trusting the cached
    // account first.
    store.user = isUnifiedCommerce()
      ? clientAccountParam || cachedSupportAccount
      : cachedSupportAccount || clientAccountParam
    store.isSupport = true
    globalThis?.localStorage?.setItem('support_client_account', store.user)
  }
}

function isKeycloakAuthenticationError(error) {
  return error.message.includes('Keycloak user is not found')
}

function isAuthenticationError(error) {
  const errorCode = safeGet(error, 'properties.error.response.status')
  return errorCode === 401 || isKeycloakAuthenticationError(error)
}

export const useAuthStore = defineStore('auth', {
  state: () => initialState(),

  getters: {
    isAuthenticated: (state) => state.authenticated,
    // TODO: refactor once we have proper users
    isAdmin: (state) => !state.thUserId,
    isStaff: (state) => !!state.staff,
    isGastro: (state) =>
      safeGet(state, 'me.user.licenses', [])?.includes(license.GASTRO),
    isLitePos: (state) =>
      safeGet(state, 'me.user.licenses', [])?.includes(license.LITEPOS)
  },

  actions: {
    async getAuthToken({ username, password, organisation, recaptchaToken }) {
      try {
        const {
          token,
          expiresAt,
          user,
          features,
          name,
          role,
          scopes,
          subUser,
          errors,
          orgName,
          whitelabel
        } = await auth.login({
          username,
          password,
          organisation,
          recaptchaToken
        })

        const subUserDisabled =
          Array.isArray(errors) && errors.length
            ? errors.find(
                (err) =>
                  err.label === 'users.secondary.errors.sub_user.inactive'
              )
            : false

        if (subUserDisabled) {
          throw new Error(subUserDisabled.label)
        }

        const appConfigStore = useAppConfigStore()
        this.locations = null
        useLocationStore().$reset()
        this.token = token
        this.user = user
        this.accountName = name
        this.orgName = orgName
        this.expiresAt = expiresAt
        this.setUsername(username)
        this.features = features || {}
        this.whitelabel = whitelabel || {}
        appConfigStore.setFeatureFlagsOnConfig(features, whitelabel)
        this.role = role
        this.scopes = [...(scopes || [])]
        this.locations = Array.isArray(safeGet(subUser, 'locations'))
          ? [...safeGet(subUser, 'locations')]
          : null
        this.thUserId = safeGet(subUser, 'id') || null
        this.isSupport = null
        localStorage.removeItem(LocalStorageKeys.SupportUserId)
        localStorage.removeItem('support_client_account')
        await auth.handleAuthentication({ token, expiresAt, user })
        return [null, { token, user }]
      } catch (err) {
        this.token = null
        return [err]
      }
    },

    async getSupportAuthToken({ token: supportToken, clientAccount }) {
      try {
        const {
          token,
          expiresAt,
          user,
          features,
          name,
          username,
          role,
          scopes,
          whitelabel
        } = await auth.loginAsSupport({
          token: supportToken,
          clientAccount
        })

        const appConfigStore = useAppConfigStore()

        this.token = token
        this.user = user
        this.accountName = name
        this.setUsername(username || null)
        this.features = features || {}
        this.whitelabel = whitelabel || {}
        appConfigStore.setFeatureFlagsOnConfig(features, whitelabel)
        this.role = role
        this.scopes = [...(scopes || [])]
        this.isSupport = true
        await auth.handleAuthentication({ token, expiresAt, user })

        // When logging in as support user, the support user id is only available in the jwt
        // token which endor generates, which is only available during login. Since we want to capture
        // this as part of posthog events, we will save it in local storage and refer to it later when
        // identifying the user in subsequent sessions
        const supportUserId = jwt_decode(supportToken)?.user?.support_user

        globalThis.localStorage.setItem(
          LocalStorageKeys.SupportUserId,
          supportUserId
        )

        return [null, { token, user }]
      } catch (err) {
        this.token = null
        return [err]
      }
    },

    logout() {
      const posthog = usePosthog()
      const wasAuthenticated = this.authenticated

      this.authenticated = false
      this.token = null
      this.user = null
      this.email = null

      globalThis.localStorage.removeItem('token')
      globalThis.localStorage.removeItem(LocalStorageKeys.SupportUserId)
      globalThis.localStorage.removeItem(LocalStorageKeys.SessionUser)
      globalThis.localStorage.removeItem('support_client_account')
      globalThis.localStorage.removeItem('clientAccountId')
      globalThis.localStorage.removeItem('iam-users-tab-banner-dismissed')

      if (wasAuthenticated) {
        posthog.capture('logout')
      }
      posthog.resetUser()
      auth.logout()
    },

    clearStateOnLogin() {
      try {
        globalThis.localStorage.removeItem('token')
        globalThis.localStorage.removeItem('token2FACreationDate')
        globalThis.localStorage.removeItem(LocalStorageKeys.SessionUser)
        globalThis.localStorage.removeItem('clientAccountId')
      } catch {
        // no-op
      }
      this.token = null
      this.user = null
      this.locations = null
    },

    applyApplicationLoginSession({ token, user, expiresAt }) {
      this.locations = null
      useLocationStore().$reset()
      this.token = token
      this.user = user
      this.expiresAt = expiresAt
    },

    setOwnerRoleAndAllScopes() {
      this.role = 'owner'
      this.scopes = ['all']
    },

    setUsername(username) {
      // TODO: the username for sub users is not available from the token or from the me call yet. We need to store it in the local storage for now.
      const normalizedUsername =
        typeof username === 'string' ? username.trim() : ''
      this.username = normalizedUsername || null
      if (normalizedUsername) {
        globalThis?.localStorage?.setItem(
          LocalStorageKeys.SessionUser,
          normalizedUsername
        )
      } else {
        globalThis?.localStorage?.removeItem(LocalStorageKeys.SessionUser)
      }
    },

    setUser(user) {
      this.user = user
    },

    setIsSupport(value) {
      this.isSupport = value
    },

    setAuthenticated(value) {
      this.authenticated = value
    },

    setInitialized(value) {
      this.isThInitialized = value
    },

    setFeatures(features) {
      this.features = features
    },

    setCredentialsFromToken(token) {
      this.authenticated = !!token

      if (token) {
        // Assign support user flag and TH user id from the token
        const decodedToken = jwt_decode(token)
        this.isSupport = decodedToken?.is_support === true
        this.thUserId = decodedToken?.sub_user || null
      }
    },

    // Function that waits till th is initilized
    async waitThInitializated() {
      return new Promise((resolve) => {
        ;(function waitForTHInitializated(store) {
          if (store.isThInitialized) return resolve()
          setTimeout(() => waitForTHInitializated(store), 30)
        })(this)
      })
    },

    /**
     * Ensures `/me` has been loaded once for authenticated sessions (e.g. router guards vs App.vue).
     * Concurrent callers share a single in-flight `getMeData` request.
     */
    async waitForMeData() {
      if (!this.isAuthenticated) return
      if (this.me) return
      if (!waitForMeDataInflight) {
        waitForMeDataInflight = this.getMeData().finally(() => {
          waitForMeDataInflight = null
        })
      }
      return waitForMeDataInflight
    },

    async getMeData({ allowLogout = true } = {}) {
      if (!this.isAuthenticated) return
      try {
        await getSupportUserCredentials(this)
        const posthog = usePosthog()

        const { data, errors } = await this.fetchMeWithRetry(() =>
          th.me().get(this.isSupport ? this.user : getSwitchedClientAccount())
        )

        if (isUnifiedCommerce()) {
          await this.applyUnifiedCommerceMe(data)
        }

        this.me = data
        this.applyBaseIdentity(data)

        const supportUserId = this.applyKeycloakIdentity(data)
        posthog.identifyUser(data.user, this.username, supportUserId)

        this.acknowledgeFirstLoginIfNeeded()

        this.applyFeatureFlags(data)
        this.applyScopesAndStaff(data)
        this.applyLocations(data)
        this.setLocalMessagesFromErrors(errors)
      } catch (error) {
        this.handleMeError(error, allowLogout)
      }
    },

    async fetchMeWithRetry(fn) {
      return pRetry(fn, {
        retries: 3,
        onFailedAttempt: async (error) => {
          if (isAuthenticationError(error)) {
            throw new pRetry.AbortError(error.message)
          }
        }
      })
    },

    async applyUnifiedCommerceMe(data) {
      const uodMe = async () => {
        const clientId =
          globalThis?.localStorage?.getItem('clientAccountId') ||
          getClientAccountId() ||
          this.user
        return th.iamMeClass().get(this.isSupport ? this.user : clientId)
      }

      const uodMeResult = await this.fetchMeWithRetry(uodMe)
      data.user.scopes = uodMeResult?.data?.scopes || data?.user?.scopes
      this.setLocalMessagesFromErrors(uodMeResult?.errors)
    },

    applyBaseIdentity(data) {
      this.accountName = data.user.name
      if (data.user.display_name) this.orgName = data.user.display_name
      this.role = data.user.role
      this.scopes = [...(data.user.scopes || [])]
      this.features = data.user.features || {}
      if (data.user.whitelabel) this.whitelabel = data.user.whitelabel
    },

    applyKeycloakIdentity(data) {
      const cachedSupportUserId =
        globalThis.localStorage.getItem(LocalStorageKeys.SupportUserId) ||
        undefined

      // Support login may not return a username (e.g. loginAsSupport response
      // omits it); backfill it from /me so authStore.username is never left
      // empty for an authenticated session.
      if (!this.username) {
        this.setUsername(data.user.username || data.user.email || null)
      }

      if (!(isUnifiedCommerce() && isKeycloackEnabled())) {
        return cachedSupportUserId
      }

      // On keycloak login we don't have the client id, so we retrieve it from /me
      this.user = data.user.id
      this.orgName = data.user.name
      this.setUsername(data.user.username || data.user.email || null)
      return this.isSupport ? getUserId() : cachedSupportUserId
    },

    applyFeatureFlags(data) {
      if (!data.user.features) return
      this.features = data.user.features
      useAppConfigStore().setFeatureFlagsOnConfig(
        data.user.features,
        data.user.whitelabel
      )
    },

    applyScopesAndStaff(data) {
      const userScopes = safeGet(data, 'user.scopes') || []
      const connectedStaff = safeGet(data, 'user.connected_staff') || []
      const staffScopes = connectedStaff.reduce(
        (acc, item) => acc.concat(item.scopes || []),
        []
      )
      this.staff = connectedStaff[0]?.id || null
      this.scopes = [...new Set([...userScopes, ...staffScopes])]
    },

    applyLocations(data) {
      const userLocations = safeGet(data, 'user.locations')
      const userIsEverywhere = userLocations === null
      const userLocationsArray =
        !userIsEverywhere && Array.isArray(userLocations) ? userLocations : []
      const staffLocations = (
        safeGet(data, 'user.connected_staff') || []
      ).reduce((acc, item) => acc.concat(item.locations || []), [])

      const everywhere = this.isAdmin || !!this.isSupport || userIsEverywhere
      this.locations = everywhere
        ? null
        : [...new Set([...userLocationsArray, ...staffLocations])]
    },

    setLocalMessagesFromErrors(errors) {
      if (!errors?.length) return
      const messagesStore = useMessagesStore()
      errors.forEach((errorObj) => {
        messagesStore.setLocalMessage({
          id: errorObj.id,
          label: errorObj.label,
          operation: 'local_message',
          payload: errorObj.errorDetails
        })
      })
    },

    handleMeError(error, allowLogout) {
      const app = globalThis?.app?.config?.globalProperties
      if (!app) return
      app.$logException(error, {
        trackError: isKeycloakAuthenticationError(error)
      })
      if (allowLogout && isAuthenticationError(error)) {
        this.logout()
      }
    },

    async acknowledgeFirstLoginIfNeeded() {
      if (!(isUnifiedCommerce() && isKeycloackEnabled())) return
      if (this.isSupport || firstLoginAcknowledged || getIsFirstLoginDone()) {
        return
      }
      firstLoginAcknowledged = true

      try {
        const tenantId = getClientAccountId()
        await th.iamUsers().acknowledgeFirstLogin(tenantId)

        await auth.refreshKeycloakToken()
        const { useConnectedDashboardsStore } =
          await import('@shared/store/connected-dashboards')
        const mmsHierarchyEnabled =
          await usePosthog().isFeatureEnabledWhenReady(
            FeatureFlags.UOD_MMS_HIERARCHY
          )
        if (mmsHierarchyEnabled) {
          await useConnectedDashboardsStore().loadConnectedDashboards()
        }
      } catch (error) {
        firstLoginAcknowledged = false
        globalThis?.app?.config?.globalProperties?.$logException?.(error, {
          trackError: false
        })
      }
    },

    async reScopeToClientAccount(clientAccountId) {
      if (clientAccountId) {
        globalThis.localStorage.setItem('clientAccountId', clientAccountId)
      } else {
        const primary = getClientAccountId()
        if (primary) {
          globalThis.localStorage.setItem('clientAccountId', primary)
        } else {
          globalThis.localStorage.removeItem('clientAccountId')
        }
      }

      const baseUrl = await getBaseUrl()
      const token = globalThis.localStorage.getItem('token') || ''
      auth.initializeSDK(
        baseUrl,
        token,
        globalThis.localStorage.getItem('clientAccountId') || undefined
      )

      this.me = null
      try {
        await this.getMeData({ allowLogout: false })
      } catch {}
    }
  }
})
